import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Users2, Search, Star, Phone, Lock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth, isRespAdmin, RUOLI_LABEL } from "@/context/AuthContext";
import { BottomSheet, Empty, DeleteButton, Field } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

const RUOLO_BADGE = {
  responsabile: { label: "Referente", cls: "text-emerald-600" },
  animatore: { label: "Animatore", cls: "text-amber-500" },
  aiuto_animatore: { label: "Aiuto", cls: "text-sky-500" },
};

/**
 * Selettore multiplo dei membri.
 * Chi è già in un altro gruppo (animatori e aiuti: un gruppo solo) compare
 * disabilitato con l'indicazione del gruppo che lo ha già preso.
 */
function MembriPicker({ utenti, selected, onToggle, occupati, max = 40 }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const filtered = query
    ? utenti.filter((u) => `${u.nome} ${u.cognome} ${u.username}`.toLowerCase().includes(query))
    : utenti.filter((u) => selected.includes(u.id));
  const shown = filtered.slice(0, max);

  return (
    <div>
      <div className="relative mb-3">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="search"
          className="input-app !pl-11 !py-2.5"
          placeholder="Cerca per nome, cognome o nome utente..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          // Il campo vive dentro il form del gruppo: Invio non deve salvare
          onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
          data-testid="gruppo-membri-search"
        />
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {selected.map((id) => {
            const u = utenti.find((x) => x.id === id);
            if (!u) return null;
            const ref = u.role === "responsabile";
            return (
              <span key={id}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${ref ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                data-testid={`gruppo-chip-${id}`}>
                {ref && <Star className="h-3 w-3 fill-current" />}
                {u.nome} {u.cognome}
                <button type="button" onClick={() => onToggle(id)} className="hover:opacity-70" aria-label="Rimuovi" data-testid={`gruppo-remove-${id}`}>×</button>
              </span>
            );
          })}
        </div>
      )}

      {query ? (
        <div className="max-h-52 overflow-y-auto space-y-1 border border-slate-100 rounded-2xl p-1.5">
          {shown.length === 0 ? (
            <p className="text-xs text-slate-400 font-semibold text-center py-4">Nessuno trovato</p>
          ) : shown.map((u) => {
            const isSel = selected.includes(u.id);
            const bloccatoDa = !isSel ? occupati[u.id] : null;
            const badge = RUOLO_BADGE[u.role] || { label: u.role, cls: "text-slate-400" };
            return (
              <button key={u.id} type="button" disabled={!!bloccatoDa}
                onClick={() => onToggle(u.id)}
                className={`w-full text-left rounded-xl px-3 py-2 text-sm font-semibold flex items-center justify-between gap-2 transition-colors ${
                  bloccatoDa ? "opacity-50 cursor-not-allowed" : isSel ? "bg-amber-50 text-amber-800" : "hover:bg-slate-50 text-slate-700"
                }`}
                data-testid={`gruppo-item-${u.id}`}>
                <span className="min-w-0 truncate">{u.nome} {u.cognome}</span>
                {bloccatoDa ? (
                  <span className="text-[11px] text-slate-400 shrink-0 inline-flex items-center gap-1">
                    <Lock className="h-3 w-3" /> in «{bloccatoDa}»
                  </span>
                ) : (
                  <span className={`text-xs shrink-0 ${badge.cls}`}>{badge.label}</span>
                )}
              </button>
            );
          })}
          {filtered.length > shown.length && (
            <p className="text-[10px] text-slate-400 text-center py-1 font-semibold">…e altri {filtered.length - shown.length}. Restringi la ricerca.</p>
          )}
        </div>
      ) : (
        selected.length === 0 && (
          <p className="text-xs text-slate-400 font-semibold text-center py-3">Inizia a digitare per cercare una persona</p>
        )
      )}
    </div>
  );
}

export default function Gruppi() {
  const { user } = useAuth();
  const puoModificare = isRespAdmin(user);
  const [gruppi, setGruppi] = useState(null);
  const [utenti, setUtenti] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nome: "", membri_ids: [] });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    api.get("/gruppi").then((r) => setGruppi(r.data));
    if (isRespAdmin(user)) api.get("/utenti").then((r) => setUtenti(r.data));
  }, [user]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  // Mappa "id utente → nome del gruppo che lo ha già preso" (solo animatori e aiuti)
  const occupati = useMemo(() => {
    const m = {};
    (gruppi || []).forEach((g) => {
      if (g.id === editId) return; // nel gruppo che sto modificando non è un conflitto
      g.staff?.forEach((s) => { m[s.id] = g.nome; });
    });
    return m;
  }, [gruppi, editId]);

  const apri = (g = null) => {
    setEditId(g?.id || null);
    setForm(g ? { nome: g.nome, membri_ids: g.membri.map((m) => m.id) } : { nome: "", membri_ids: [] });
    setSheetOpen(true);
  };

  const toggleMembro = (id) =>
    setForm((f) => ({
      ...f,
      membri_ids: f.membri_ids.includes(id) ? f.membri_ids.filter((x) => x !== id) : [...f.membri_ids, id],
    }));

  const nReferenti = form.membri_ids.filter(
    (id) => utenti.find((u) => u.id === id)?.role === "responsabile"
  ).length;

  const salva = async (e) => {
    e.preventDefault();
    if (nReferenti === 0) {
      toast.error("Aggiungi almeno un Responsabile come referente del gruppo");
      return;
    }
    setSaving(true);
    try {
      const payload = { nome: form.nome.trim(), membri_ids: form.membri_ids };
      if (editId) await api.put(`/gruppi/${editId}`, payload);
      else await api.post("/gruppi", payload);
      toast.success("Gruppo salvato");
      setSheetOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async () => {
    try {
      await api.delete(`/gruppi/${editId}`);
      toast.success("Gruppo eliminato");
      setSheetOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!gruppi) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  const q = search.trim().toLowerCase();
  const lista = q
    ? gruppi.filter((g) =>
        `${g.nome} ${g.membri.map((m) => `${m.nome} ${m.cognome}`).join(" ")}`.toLowerCase().includes(q))
    : gruppi;

  return (
    <div data-testid="gruppi-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Gruppi</h1>
          <p className="text-sm text-slate-500 font-semibold">Chi organizza la giornata dell'oratorio</p>
        </div>
        {puoModificare && (
          <button className="btn-primary" onClick={() => apri()} data-testid="nuovo-gruppo-btn">
            <Plus className="h-4 w-4" /> Nuovo gruppo
          </button>
        )}
      </div>

      {gruppi.length === 0 ? (
        <Empty icon={Users2} text="Nessun gruppo"
          sub={puoModificare ? "Crea il primo gruppo dal pulsante in alto" : "I Responsabili non hanno ancora creato i gruppi"} />
      ) : (
        <>
          <div className="relative mb-5">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="search" className="input-app !pl-11" placeholder="Cerca gruppo o persona..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              data-testid="gruppi-search-input" />
          </div>

          {lista.length === 0 ? (
            <div className="card-app p-6 text-center"><p className="text-sm text-slate-400 font-semibold">Nessun gruppo corrisponde alla ricerca</p></div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {lista.map((g, i) => (
                <motion.div
                  key={g.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.4) }}
                  onClick={() => puoModificare && apri(g)}
                  className={`card-app p-5 ${puoModificare ? "cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all" : ""}`}
                  data-testid={`gruppo-${g.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="font-display font-extrabold text-slate-900 text-lg break-words min-w-0">{g.nome}</h2>
                    <span className="badge-info shrink-0">{g.tot_membri} membr{g.tot_membri === 1 ? "o" : "i"}</span>
                  </div>

                  {g.referenti.length > 0 && (
                    <div className="mb-3">
                      <p className="label-app">{g.referenti.length === 1 ? "Referente" : "Referenti"}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {g.referenti.map((r) => (
                          <span key={r.id} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1 text-xs font-bold" data-testid={`referente-${r.id}`}>
                            <Star className="h-3 w-3 fill-current" />
                            {r.nome} {r.cognome}
                            {r.telefono && <a href={`tel:${r.telefono}`} onClick={(e) => e.stopPropagation()} className="text-emerald-600 hover:text-emerald-900" aria-label={`Chiama ${r.nome}`}><Phone className="h-3 w-3" /></a>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {g.staff.length > 0 ? (
                    <div>
                      <p className="label-app">Animatori</p>
                      <div className="flex flex-wrap gap-1.5">
                        {g.staff.map((m) => (
                          <span key={m.id} className="rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-semibold" data-testid={`membro-${m.id}`}>
                            {m.nome} {m.cognome}
                            {m.role === "aiuto_animatore" && <span className="text-sky-500 font-bold"> · Aiuto</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 font-semibold">Nessun animatore assegnato</p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {puoModificare && (
        <BottomSheet open={sheetOpen} onOpenChange={setSheetOpen}
          title={editId ? "Modifica gruppo" : "Nuovo gruppo"}
          description="Ogni gruppo deve avere almeno un Responsabile come referente. Animatori e aiuti possono stare in un solo gruppo."
          testId="gruppo-sheet">
          <form onSubmit={salva}>
            <Field label="Nome del gruppo">
              <input className="input-app" required value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Es. Gruppo Accoglienza, Gruppo Giochi..."
                data-testid="gruppo-nome-input" />
            </Field>

            <label className="label-app">Membri del gruppo</label>
            <MembriPicker utenti={utenti} selected={form.membri_ids} onToggle={toggleMembro} occupati={occupati} />

            <div className={`rounded-2xl p-3 my-4 text-xs font-bold flex items-center justify-between gap-2 ${nReferenti === 0 ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-50 text-slate-600"}`} data-testid="gruppo-riepilogo">
              <span>{nReferenti === 0 ? "Serve almeno un Responsabile come referente" : `${nReferenti} referent${nReferenti === 1 ? "e" : "i"}`}</span>
              <span className="text-slate-500">{form.membri_ids.length} membri in tutto</span>
            </div>

            <button className="btn-primary w-full" disabled={saving || nReferenti === 0} data-testid="gruppo-submit-btn">
              {saving ? "Salvataggio..." : "Salva gruppo"}
            </button>
            {editId && (
              <div className="mt-3 flex justify-end">
                <DeleteButton onConfirm={elimina} entity="questo gruppo" testId="gruppo-delete-btn" />
              </div>
            )}
          </form>
        </BottomSheet>
      )}
    </div>
  );
}
